/**
 * Service to authenticate user.
 *
 * It requires to set up this field into isophi-core library:
 * - auth-api
 * - client-id
 * - user-repository: Optional for offline login.
 * - logged-teacher-api / logged-parent-api: Optional, merged-portal APIs fetched in parallel with the user.
 */
import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { DismissedPopupKeywords, User } from '../../models';
import { IStorage } from '../../storages';
import { HttpService } from './http.service';
import { IsophiCoreService } from './isophi-core.service';
import { SecurityService } from './security.service';
import { BehaviorSubject, ReplaySubject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public loggedUser: User | null = null;

  public loggedUser$ = new BehaviorSubject<User | null>(null);

  public accessToken: string | null = null;

  public accessTokenStorageKey = 'isophiAccessToken';

  public readonly onLogout$ = new ReplaySubject<void>(void 0);

  protected readonly httpService = inject(HttpService);
  protected readonly securityService = inject(SecurityService);
  protected readonly isophiCoreService = inject(IsophiCoreService);

  /**
   * Logout user - clear user data and access token
   */
  public logoutUser(storage: IStorage): void {
    storage.removeOnLogout();
    this.loggedUser = null;
    this.loggedUser$.next(null);
    this.accessToken = null;
    this.onLogout$.next();
  }

  /**
   * Save token to storage.
   *
   * @param storage
   */
  public storeToken(storage: IStorage): void {
    storage.setItem(this.accessTokenStorageKey, this.accessToken, true);
  }

  /**
   * Load token from storage.
   */
  public restoreToken(storage: IStorage): void {
    this.accessToken = storage.getItem(this.accessTokenStorageKey);
  }

  /**
   * Check if token is valid.
   *
   * @returns       True/False if this.accessToken is valid and active.
   */
  public isAccessTokenValid(): Promise<boolean> {
    if (!this.accessToken) return Promise.resolve(false);

    const authUrl = `${this.isophiCoreService.authApi}/introspect/`;
    const postParams = new Map([['token', this.accessToken]]);

    return this.httpService
      .post(authUrl, postParams, this.accessToken)
      .toPromise()
      .then((res) => {
        return res.body.active;
      })
      .catch(() => {
        return false;
      });
  }

  /**
   * Authenticate user by access token saved in this service.
   * If success, it returns true and it saves authenticated User into this.loggedUser,
   * else it returns false.
   */
  public authenticateByAccessToken(): Promise<void> {
    if (!this.accessToken) return Promise.resolve();

    return this.fetchLoggedUser().then((user) => {
      this.loggedUser = user;
      this.loggedUser$.next(this.loggedUser);
    });
  }

  public updateUser(update: Partial<User>) {
    this.loggedUser = Object.assign(this.loggedUser, update);
    this.loggedUser$.next(this.loggedUser);
  }

  /**
   * Authenticate user.
   * If success, it returns true and it saves authenticated User into this.loggedUser,
   * else it returns false.
   *
   * If onlineLogin is true, it connects to server.
   * Else it does offline login.
   *
   * @param username
   * @param password
   * @param onlineLogin
   */
  public authenticate(username: string, password: string, onlineLogin = true): Promise<void> {
    if (onlineLogin) {
      return this.serverAuthentication(username, password);
    } else {
      return this.offlineAuthentication(username, password);
    }
  }

  /**
   * Authenticate user offline, it uses Users saved in the Application.
   * If success, it returns authenticated User else it returns null.
   *
   * @param username
   * @param password
   */
  protected offlineAuthentication(username: string, password: string): Promise<void> {
    const userRepository = this.isophiCoreService.userRepository;
    const user = userRepository.getUserByUsername(username, false);

    if (user === undefined) return Promise.reject('User not found in repository.');

    let isPasswordCorrect = false;
    try {
      isPasswordCorrect = this.securityService.checkPassword(password, user.password);
    } catch (e) {
      console.error(e);
    }

    if (isPasswordCorrect) {
      this.loggedUser = user;
      this.loggedUser$.next(this.loggedUser);
      return Promise.resolve();
    } else {
      return Promise.reject('Invalid user credentials.');
    }
  }

  /**
   * Authenticate user in the auth server. If success, it returns authenticated User else it returns null.
   *
   * @param username
   * @param password
   */
  protected serverAuthentication(username: string, password: string): Promise<void> {
    const authUrl = `${this.isophiCoreService.authApi}/token/`;
    const urlParams = new Map<string, any>();
    urlParams.set('grant_type', 'password');
    urlParams.set('client_id', this.isophiCoreService.clientId);
    urlParams.set('username', username);
    urlParams.set('password', password);

    // todo(doubravskytomas): log reason, maybe show specific message to user by error code ?
    return this.httpService
      .post(authUrl, urlParams, null, 'application/x-www-form-urlencoded')
      .toPromise()
      .then((response) => {
        if (response.status !== 200) return Promise.reject('Invalid response code - access token.');

        const data = response.body;
        this.accessToken = data.access_token;
        return this.fetchLoggedUser().then((user) => {
          this.loggedUser = user;
          this.loggedUser$.next(this.loggedUser);
        });
      });
  }

  /**
   * Fetch logged user from auth-portal together with its teacher/parent model from merged-portal.
   *
   * The requests run in parallel; previously auth-portal fetched the models itself (`extra_models`)
   * and blocked its worker until merged-portal answered.
   *
   * Backwards compatibility: when merged-portal does not expose the logged-model endpoint yet (404),
   * it falls back to the legacy `extra_models` request, so the FE works against an older BE.
   */
  protected fetchLoggedUser(): Promise<User> {
    return Promise.all([
      this.fetchUserData(this.userUrl()),
      this.fetchLoggedModel(this.isophiCoreService.loggedTeacherApi, '/logged-teacher/?extra_fields=kindergarten_ids,kindergartens'),
      this.fetchLoggedModel(this.isophiCoreService.loggedParentApi, '/logged-parent/'),
    ]).then(([userData, teacher, parent]) => {
      if (teacher === AuthService.MODEL_ENDPOINT_MISSING || parent === AuthService.MODEL_ENDPOINT_MISSING) {
        return this.fetchUserData(this.userUrl(this.legacyExtraModels())).then((legacyUserData) => User.deserialize(legacyUserData));
      }

      if (teacher !== undefined) userData.teacher = teacher;
      if (parent !== undefined) userData.parent = parent;
      return User.deserialize(userData);
    });
  }

  /** Marker: merged-portal answered 404, i.e. the logged-model endpoint is not deployed yet. */
  protected static readonly MODEL_ENDPOINT_MISSING = Symbol('logged-model-endpoint-missing');

  protected fetchUserData(url: string): Promise<any> {
    return this.httpService
      .get(url, this.accessToken)
      .toPromise()
      .then((userResponse) => {
        if (userResponse.status !== 200) return Promise.reject('Invalid response code - user.');
        return userResponse.body;
      });
  }

  /**
   * Fetch teacher/parent model of the logged user from merged-portal.
   *
   * Resolves to undefined when the API is not configured for the app, to MODEL_ENDPOINT_MISSING when
   * merged-portal does not know the endpoint (older BE), and to null when the user has no such model
   * or the service failed - the same as auth-portal `extra_models` returned.
   */
  protected fetchLoggedModel(apiUrl: string | null, path: string): Promise<object | null | undefined | symbol> {
    if (!apiUrl) return Promise.resolve(undefined);

    return this.httpService
      .get(`${apiUrl}${path}`, this.accessToken)
      .toPromise()
      .then((response) => (response.status === 200 ? response.body : null))
      .catch((error: HttpErrorResponse) => (error?.status === 404 ? AuthService.MODEL_ENDPOINT_MISSING : null));
  }

  /**
   * `extra_models` value equivalent to the configured logged-model APIs, for the legacy auth-portal path.
   */
  protected legacyExtraModels(): string {
    const models: string[] = [];
    if (this.isophiCoreService.loggedTeacherApi) models.push('teacher');
    if (this.isophiCoreService.loggedParentApi) models.push('parent');
    return models.join(',');
  }

  /**
   * Generate URL for fetching User.
   *
   * `extra_models` is the legacy path where auth-portal proxies merged-portal models itself;
   * it defaults to the `auth-extra-models` field (still used by apps not migrated to logged-*-api).
   *
   * @protected
   */
  protected userUrl(extraModels: string | null = this.isophiCoreService.authExtraModels): string {
    let userUrl = `${this.isophiCoreService.authApi}/logged-user/?extra_fields=password_data,group_string`;
    if (extraModels) {
      userUrl += `&extra_models=${extraModels}`;
    }
    return userUrl;
  }

  dismissUserPopup(popup: DismissedPopupKeywords) {
    if (!this.loggedUser.teacher.dismissedPopupKeywords.some((key) => popup === key)) {
      this.loggedUser.teacher.dismissedPopupKeywords.push(popup);
      this.loggedUser$.next(this.loggedUser);
    }
  }
}
